/**
 * Boorprofiel-classificatiestandaard per (deel)project.
 *
 * NEN 5104 is de default, omdat vrijwel iedereen daar in de praktijk nog mee werkt. NEN 6693
 * is de nieuwere systematiek die binnenkort verplicht wordt; een project kan er nu al op
 * gezet worden.
 * De gekozen standaard bepaalt hoe een ingesproken grondmonster tot een boorprofiel wordt
 * verwerkt (AI-extractie) én hoe het laag-bewerkformulier in de backoffice de codelijsten toont.
 */

export type SoilStandard = 'nen5104' | 'nen6693' | 'iso14688';

export interface SoilStandardDef {
  id: SoilStandard;
  label: string; // volledige weergavenaam
  short: string; // korte weergave (tegel/label)
  selectable: boolean; // false = bekend in het model, maar nog niet te kiezen in de keuzelijsten
}

// Volgorde telt: de keuzelijsten lopen deze lijst af, dus de default hoort bovenaan.
// NEN-EN-ISO 14688 is kiesbaar sinds AI-extractie, laagformulier en export zijn aangesloten
// (op de NEN 6693-basis). Nog open: de TerraIndex-codes voor organisch, korrelhoekigheid en
// vorm, en de korrelverdeling bepaalt bij ISO (nog) niet de grondsoort.
export const SOIL_STANDARDS: Record<SoilStandard, SoilStandardDef> = {
  nen5104: { id: 'nen5104', label: 'NEN 5104', short: 'NEN 5104', selectable: true },
  nen6693: { id: 'nen6693', label: 'NEN 6693', short: 'NEN 6693', selectable: true },
  iso14688: { id: 'iso14688', label: 'NEN-EN-ISO 14688', short: 'ISO 14688', selectable: true },
};

/** De standaarden die in de keuzelijsten (backoffice en veldapp) mogen staan. */
export const SELECTABLE_SOIL_STANDARDS: SoilStandardDef[] = Object.values(SOIL_STANDARDS).filter(
  (s) => s.selectable,
);

/** Default voor nieuwe projecten: NEN 5104, want daar werkt vrijwel iedereen nog mee. */
export const DEFAULT_SOIL_STANDARD: SoilStandard = 'nen5104';

/** Normaliseer een opgeslagen/ruwe waarde naar een geldige standaard (fallback = default). */
export function normalizeSoilStandard(value: string | null | undefined): SoilStandard {
  return value === 'nen5104' || value === 'nen6693' || value === 'iso14688'
    ? value
    : DEFAULT_SOIL_STANDARD;
}

/**
 * Slaat deze standaard zijn lagen op in de interne NEN 6693-codes? ISO 14688 doet dat ook: hij
 * is op NEN 6693 gebouwd (zie codelists/iso14688.ts). NEN 5104 gebruikt de TI/SIKB-codes.
 */
export function usesNen6693Codes(standard: string | null | undefined): boolean {
  return standard === 'nen6693' || standard === 'iso14688';
}

/** Is dit een bekende standaard? Voor invoer van buiten, waar onbekend geen standaard is. */
export function isSoilStandard(value: unknown): value is SoilStandard {
  return value === 'nen5104' || value === 'nen6693' || value === 'iso14688';
}
